import * as ed from '@noble/ed25519';

import { licenseMessageBytes } from './canonical.js';
import { License } from './types.js';

/**
 * Verify a license against a public key, expected tenant, and clock.
 *
 * Returns the parsed License on success, or `null` on ANY failure:
 *   - parse failure (malformed shape)
 *   - signature mismatch
 *   - tenant mismatch (license.tenantId !== expectedTenantId)
 *   - expired past its grace window (effectiveNow >= expiresAt + graceDays·86400)
 *   - any thrown error during the above
 *
 * Per the "fail to Core" principle this function never throws. The caller
 * uses null to mean "fall back to Core tier."
 *
 * Clock-rollback resistance: expiry is judged against `max(now, clockFloor)`,
 * where `clockFloor` is a monotonic high-water-mark the caller persists across
 * boots. Winding the device clock BACK therefore can't un-expire a license
 * (kiosks are physically accessible and often lack NTP). A legitimate small
 * backward correction is harmless — it only ever raises the effective time.
 *
 * Key rotation: the license's `keyId` selects a pinned public key from
 * `keyring`; a license with no `keyId` verifies against `publicKey` (the
 * default/legacy key). An unknown `keyId` fails to Core.
 *
 * Revocation: a license whose `id` is in `revokedIds` fails to Core. The caller
 * builds that set from a signature-verified revocation list.
 */
export async function verifyLicense(input: {
  /** Default/legacy key — used when the license omits `keyId`. */
  publicKey: Uint8Array;
  /** keyId → public key, for licenses tagged with a rotated key. */
  keyring?: Record<string, Uint8Array>;
  license: unknown;
  expectedTenantId: string;
  /** Clock override for tests; defaults to Date.now() / 1000. */
  now?: () => number;
  /** Persisted max wall-clock ever observed (seconds). Anti-rollback floor. */
  clockFloor?: number;
  /** License ids known revoked (from a verified revocation list). */
  revokedIds?: ReadonlySet<string>;
}): Promise<License | null> {
  try {
    const parsed = License.safeParse(input.license);
    if (!parsed.success) return null;

    const license = parsed.data;
    if (license.tenantId !== input.expectedTenantId) return null;

    // Revoked → Core, even if the signature is otherwise valid.
    if (license.id !== undefined && input.revokedIds?.has(license.id)) return null;

    // Resolve the signing key: keyId picks from the keyring; absent → default.
    const publicKey =
      license.keyId !== undefined ? input.keyring?.[license.keyId] : input.publicKey;
    if (publicKey === undefined) return null; // unknown keyId → can't verify

    const now = input.now ? input.now() : Math.floor(Date.now() / 1000);
    const effectiveNow = input.clockFloor !== undefined ? Math.max(now, input.clockFloor) : now;
    if (license.expiresAt !== undefined) {
      const graceSec = (license.graceDays ?? 0) * 86_400;
      // Valid strictly before the grace window ends (grace 0 → hard expiry at
      // expiresAt, preserving the original `expiresAt <= now` boundary).
      if (effectiveNow >= license.expiresAt + graceSec) return null;
    }

    const sigBytes = ed.etc.hexToBytes(license.signature);
    const ok = await ed.verifyAsync(sigBytes, licenseMessageBytes(license), publicKey);
    return ok ? license : null;
  } catch {
    return null;
  }
}
